import { useEffect, useMemo, useState } from "react";

import { extractORBFeatures } from "../vision/features";
import { matchDescriptors } from "../vision/matching";
import { trackSemiDenseFeatures } from "../vision/dense";
import { debugLog } from "../../../utils/debug";

import {
  computeDenseDisparity,
  filterDenseDisparity,
  propagateDenseDisparity,
  fillDenseDisparityHoles,
  disparityToPointCloud,
} from "../vision/stereo";

import {
  computeDenseDisparityWebGPU,
  isWebGPUStereoAvailable,
} from "../vision/webgpuStereo";

import {
  detectSemiDenseCandidates,
  matchEpipolarCandidates,
  filterMatchesByBidirectionalConsistency,
} from "../vision/epipolarMatching";

import {
  buildCorrespondences,
  estimateFundamentalRANSAC,
  createCameraMatrix,
  fundamentalToEssential,
  recoverCameraPose,
  filterCorrespondencesByEpipolarGeometry,
  triangulateCorrespondences,
  filterTriangulatedByReprojection,
  sampsonError,
} from "../vision/geometry";

import { buildDenseMesh } from "../vision/mesh";

import {
  extractImageIntensity,
  loadImageElement,
  sampleImageIntensity,
} from "../vision/imageUtils";

import { estimateSharedFocalLength } from "../vision/focalCalibration";
import { rectifyUncalibratedPair } from "../vision/rectification";

import {
  evaluateSparseReprojection,
  evaluatePoseFundamental,
  evaluateEssentialRoundTrip,
  evaluateRawEssentialRoundTrip,
  evaluateRawEssentialReprojection,
} from "../vision/reconstructionDiagnostics";

async function resizeImageForDenseStereo(
  imageElement,
  maximumDimension = 1200,
) {
  const sourceWidth = imageElement.naturalWidth || imageElement.width;
  const sourceHeight = imageElement.naturalHeight || imageElement.height;
  const sourceMaximumDimension = Math.max(sourceWidth, sourceHeight);
  const scale = Math.min(1, maximumDimension / sourceMaximumDimension);

  if (scale === 1) {
    return {
      image: imageElement,
      scale: 1,
      width: sourceWidth,
      height: sourceHeight,
    };
  }

  const width = Math.max(1, Math.round(sourceWidth * scale));
  const height = Math.max(1, Math.round(sourceHeight * scale));
  const canvas = document.createElement("canvas");

  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext("2d");

  if (!context) {
    throw new Error("Unable to create canvas for dense stereo resizing.");
  }

  context.drawImage(imageElement, 0, 0, width, height);

  const resizedImage = new Image();

  await new Promise((resolve, reject) => {
    resizedImage.onload = resolve;
    resizedImage.onerror = () => {
      reject(new Error("Unable to create resized rectified image."));
    };
    resizedImage.src = canvas.toDataURL("image/png");
  });

  return {
    image: resizedImage,
    scale,
    width,
    height,
  };
}

export default function useReconstructionPipeline({
  imageA,
  imageB,
  calibration,
  inputMode,
  featureCount,
  ratioThreshold,
  runId,
}) {
  const [featuresA, setFeaturesA] = useState(null);
  const [featuresB, setFeaturesB] = useState(null);
  const [featureStatus, setFeatureStatus] = useState("Waiting");
  const [denseStatus, setDenseStatus] = useState("idle");
  const [imageAIntensityData, setImageAIntensityData] = useState(null);
  const [denseDisparity, setDenseDisparity] = useState(null);
  const [propagatedDenseDisparity, setPropagatedDenseDisparity] =
    useState(null);
  const [denseTracks, setDenseTracks] = useState([]);
  const [epipolarMatches, setEpipolarMatches] = useState([]);
  const [rectification, setRectification] = useState(null);

  useEffect(() => {
    if (!imageA || !imageB) {
      setDenseStatus("idle");
      return;
    }

    setDenseStatus("running");
  }, [runId, imageA, imageB]);

  // Sparse ORB features
  useEffect(() => {
    setFeaturesA(null);
    setFeaturesB(null);

    if (!imageA || !imageB) {
      setFeatureStatus("Waiting");
      return;
    }

    let cancelled = false;

    async function runFeatureExtraction() {
      try {
        setFeatureStatus("Extracting...");

        const imageElementA = await loadImageElement(imageA.url);
        const imageElementB = await loadImageElement(imageB.url);

        const [resultA, resultB] = await Promise.all([
          extractORBFeatures(imageElementA, featureCount),
          extractORBFeatures(imageElementB, featureCount),
        ]);

        if (cancelled) {
          return;
        }

        setFeaturesA(resultA);
        setFeaturesB(resultB);
        setFeatureStatus("Ready");

        debugLog("[Reconstruction] ORB features:", {
          imageA: resultA.keypoints.length,
          imageB: resultB.keypoints.length,
          descriptorSize: resultA.descriptors.cols,
          widthA: resultA.width,
          heightA: resultA.height,
          widthB: resultB.width,
          heightB: resultB.height,
        });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.error("[Reconstruction] ORB extraction failed:", error);
        setFeaturesA(null);
        setFeaturesB(null);
        setFeatureStatus("Failed");
      }
    }

    runFeatureExtraction();

    return () => {
      cancelled = true;
    };
  }, [imageA, imageB, featureCount]);

  // Source image intensity
  useEffect(() => {
    let cancelled = false;

    async function loadIntensityData() {
      if (!imageA?.url) {
        setImageAIntensityData(null);
        return;
      }

      try {
        const imageElement = await loadImageElement(imageA.url);
        const result = extractImageIntensity(imageElement);

        if (cancelled) {
          return;
        }

        setImageAIntensityData(result);
      } catch (error) {
        console.error("[Reconstruction] Intensity extraction failed:", error);

        if (!cancelled) {
          setImageAIntensityData(null);
        }
      }
    }

    loadIntensityData();

    return () => {
      cancelled = true;
    };
  }, [imageA]);

  // Dataset dense stereo
  useEffect(() => {
    let cancelled = false;

    async function runDenseStereo() {
      if (
        inputMode !== "dataset" ||
        !imageA?.url ||
        !imageB?.url ||
        !calibration
      ) {
        setDenseDisparity(null);
        return;
      }

      try {
        setDenseDisparity(null);

        const [imageElementA, imageElementB] = await Promise.all([
          loadImageElement(imageA.url),
          loadImageElement(imageB.url),
        ]);

        if (cancelled) {
          return;
        }

        const denseOptions = {
          minDisparity: 1,
          maxDisparity: Math.min(256, calibration.disparityCount),
          blockRadius: 3,
          minimumScore: 0.75,
          ambiguityMargin: 0.015,
          leftRightThreshold: 1,
          pixelStep: 1,
        };

        let result;

        if (isWebGPUStereoAvailable()) {
          debugLog("[Reconstruction] Using WebGPU dense stereo for dataset.");

          try {
            result = await computeDenseDisparityWebGPU(
              imageElementA,
              imageElementB,
              denseOptions,
            );
          } catch (webGPUError) {
            console.error(
              "[Reconstruction] WebGPU dataset dense stereo failed; falling back to CPU:",
              webGPUError,
            );

            result = await computeDenseDisparity(
              imageElementA,
              imageElementB,
              denseOptions,
            );
          }
        } else {
          debugLog(
            "[Reconstruction] WebGPU unavailable; using CPU dense stereo for dataset.",
          );

          result = await computeDenseDisparity(
            imageElementA,
            imageElementB,
            denseOptions,
          );
        }

        if (cancelled) {
          return;
        }

        setDenseDisparity(result);
        setDenseStatus("complete");

        let validCount = 0;
        let minimumDisparity = Infinity;
        let maximumDisparity = -Infinity;

        for (const disparity of result.disparities) {
          if (!Number.isFinite(disparity)) {
            continue;
          }

          validCount += 1;
          minimumDisparity = Math.min(minimumDisparity, disparity);
          maximumDisparity = Math.max(maximumDisparity, disparity);
        }

        debugLog("[Reconstruction] Dense disparity:", {
          width: result.width,
          height: result.height,
          validCount,
          minimumDisparity: validCount > 0 ? minimumDisparity : null,
          maximumDisparity: validCount > 0 ? maximumDisparity : null,
          pixelStep: result.pixelStep,
        });
      } catch (error) {
        console.error("[Reconstruction] Dense stereo failed:", error);

        if (!cancelled) {
          setDenseDisparity(null);
          setDenseStatus("error");
        }
      }
    }

    runDenseStereo();

    return () => {
      cancelled = true;
    };
  }, [imageA, imageB, calibration, inputMode]);

  // Custom pair dense stereo
  useEffect(() => {
    let cancelled = false;

    async function runCustomDenseStereo() {
      if (
        inputMode === "dataset" ||
        !rectification?.imageA ||
        !rectification?.imageB
      ) {
        return;
      }

      try {
        setDenseDisparity(null);

        const [resizedA, resizedB] = await Promise.all([
          resizeImageForDenseStereo(rectification.imageA, 1200),
          resizeImageForDenseStereo(rectification.imageB, 1200),
        ]);

        if (cancelled) {
          return;
        }

        if (
          resizedA.width !== resizedB.width ||
          resizedA.height !== resizedB.height
        ) {
          throw new Error(
            "Resized rectified images must have identical dimensions.",
          );
        }

        const observedMinimum = rectification.diagnostics.minimumDisparity;
        const observedMaximum = rectification.diagnostics.maximumDisparity;

        if (
          !Number.isFinite(observedMinimum) ||
          !Number.isFinite(observedMaximum)
        ) {
          throw new Error(
            "Rectification did not produce a valid disparity range.",
          );
        }

        const disparityMargin = 16;

        const minDisparity = Math.floor(
          observedMinimum * resizedA.scale - disparityMargin,
        );

        const maxDisparity = Math.ceil(
          observedMaximum * resizedA.scale + disparityMargin,
        );

        debugLog("[Reconstruction] Custom dense stereo setup:", {
          originalWidth: rectification.width,
          originalHeight: rectification.height,
          denseWidth: resizedA.width,
          denseHeight: resizedA.height,
          scale: resizedA.scale,
          minDisparity,
          maxDisparity,
        });

        const denseOptions = {
          minDisparity,
          maxDisparity,
          blockRadius: 3,
          minimumScore: 0.75,
          ambiguityMargin: 0.015,
          leftRightThreshold: 1,
          pixelStep: 1,
        };

        let result;

        if (isWebGPUStereoAvailable()) {
          debugLog("[Reconstruction] Using WebGPU dense stereo.");

          try {
            result = await computeDenseDisparityWebGPU(
              resizedA.image,
              resizedB.image,
              denseOptions,
            );
          } catch (webGPUError) {
            console.error(
              "[Reconstruction] WebGPU dense stereo failed; falling back to CPU:",
              webGPUError,
            );

            result = await computeDenseDisparity(
              resizedA.image,
              resizedB.image,
              denseOptions,
            );
          }
        } else {
          debugLog(
            "[Reconstruction] WebGPU unavailable; using CPU dense stereo.",
          );

          result = await computeDenseDisparity(
            resizedA.image,
            resizedB.image,
            denseOptions,
          );
        }

        if (cancelled) {
          return;
        }

        setDenseDisparity({
          ...result,
          source: "custom-rectified",
          sourceScale: resizedA.scale,
          originalWidth: rectification.width,
          originalHeight: rectification.height,
          rectifiedImageA: resizedA.image,
          rectifiedImageB: resizedB.image,
        });

        setDenseStatus("complete");
      } catch (error) {
        console.error("[Reconstruction] Custom dense stereo failed:", error);

        if (!cancelled) {
          setDenseDisparity(null);
          setDenseStatus("error");
        }
      }
    }

    runCustomDenseStereo();

    return () => {
      cancelled = true;
    };
  }, [rectification, inputMode]);

  const filteredDenseDisparity = useMemo(() => {
    if (!denseDisparity) {
      return null;
    }

    return filterDenseDisparity(denseDisparity, {
      radius: 2,
      disparityThreshold: 2.0,
      minimumSupport: 3,
      minimumConfidence: 0.1,
    });
  }, [denseDisparity]);

  // Disparity propagation
  useEffect(() => {
    let cancelled = false;

    async function runDensePropagation() {
      if (
        inputMode !== "dataset" ||
        !imageA?.url ||
        !imageB?.url ||
        !filteredDenseDisparity
      ) {
        setPropagatedDenseDisparity(null);
        return;
      }

      try {
        setPropagatedDenseDisparity(null);

        const [imageElementA, imageElementB] = await Promise.all([
          loadImageElement(imageA.url),
          loadImageElement(imageB.url),
        ]);

        if (cancelled) {
          return;
        }

        const result = await propagateDenseDisparity(
          imageElementA,
          imageElementB,
          filteredDenseDisparity,
          {
            supportRadius: 3,
            minimumSupport: 4,
            maximumSupportSpread: 3.0,
            searchRadius: 4,
            blockRadius: 3,
            minimumScore: 0.75,
            ambiguityMargin: 0.015,
            leftRightThreshold: 1,
          },
        );

        if (cancelled) {
          return;
        }

        setPropagatedDenseDisparity(result);
      } catch (error) {
        console.error(
          "[Reconstruction] Dense disparity propagation failed:",
          error,
        );

        if (!cancelled) {
          setPropagatedDenseDisparity(null);
        }
      }
    }

    runDensePropagation();

    return () => {
      cancelled = true;
    };
  }, [imageA, imageB, filteredDenseDisparity, inputMode]);

  const surfaceDenseDisparity = useMemo(() => {
    if (!propagatedDenseDisparity) {
      return null;
    }

    return fillDenseDisparityHoles(propagatedDenseDisparity, {
      radius: 2,
      minimumSupport: 5,
      maximumSpread: 1.5,
      minimumDirectionalSupport: 1,
    });
  }, [propagatedDenseDisparity]);

  // Dense calibration
  const denseCalibration = useMemo(() => {
    if (!filteredDenseDisparity) {
      return null;
    }

    if (inputMode === "dataset") {
      return calibration;
    }

    const width = filteredDenseDisparity.width;
    const height = filteredDenseDisparity.height;
    const focalLength = Math.max(width, height);

    return {
      cameraMatrixA: createCameraMatrix(
        focalLength,
        focalLength,
        width / 2,
        height / 2,
      ),
      baseline: 1,
      disparityOffset: 0,
      useAbsoluteDisparity: true,
      isRelative: true,
    };
  }, [inputMode, calibration, filteredDenseDisparity]);

  const densePointCloud = useMemo(() => {
    if (!filteredDenseDisparity || !denseCalibration) {
      return null;
    }

    const result = disparityToPointCloud(
      filteredDenseDisparity,
      denseCalibration,
      inputMode === "dataset" ? imageAIntensityData : null,
    );

    if (result.points.length > 0) {
      const depths = result.points
        .map((point) => point.z)
        .filter(Number.isFinite)
        .sort((a, b) => a - b);

      debugLog("[Reconstruction] Dense point cloud:", {
        mode: inputMode === "dataset" ? "metric" : "relative",
        points: result.points.length,
        minimumDepth: depths[0],
        medianDepth: depths[Math.floor(depths.length / 2)],
        maximumDepth: depths[depths.length - 1],
      });
    }

    return result;
  }, [
    inputMode,
    filteredDenseDisparity,
    denseCalibration,
    imageAIntensityData,
  ]);

  const denseMesh = useMemo(() => {
    if (!filteredDenseDisparity || !denseCalibration) {
      return null;
    }

    return buildDenseMesh(filteredDenseDisparity, denseCalibration, {
      maximumRelativeDepthJump: 0.08,
    });
  }, [filteredDenseDisparity, denseCalibration]);

  // Semi-dense tracking
  useEffect(() => {
    let cancelled = false;

    async function runDenseTracking() {
      if (!imageA || !imageB) {
        setDenseTracks([]);
        return;
      }

      try {
        const [imageElementA, imageElementB] = await Promise.all([
          loadImageElement(imageA.url),
          loadImageElement(imageB.url),
        ]);

        const tracks = await trackSemiDenseFeatures(
          imageElementA,
          imageElementB,
        );

        if (cancelled) {
          return;
        }

        setDenseTracks(tracks);
        debugLog("[Reconstruction] Dense tracks ready:", tracks.length);
      } catch (error) {
        console.error("[Reconstruction] Dense tracking failed:", error);

        if (!cancelled) {
          setDenseTracks([]);
        }
      }
    }

    runDenseTracking();

    return () => {
      cancelled = true;
    };
  }, [imageA, imageB]);

  // Descriptor matching
  const matches = useMemo(() => {
    if (!featuresA || !featuresB) {
      return [];
    }

    try {
      const result = matchDescriptors(
        featuresA.descriptors,
        featuresB.descriptors,
        ratioThreshold,
      );

      debugLog("[Reconstruction] Matches:", result.length);
      return result;
    } catch (error) {
      console.error("[Reconstruction] Matching failed:", error);
      return [];
    }
  }, [featuresA, featuresB, ratioThreshold]);

  // Fundamental matrix
  const geometry = useMemo(() => {
    if (!featuresA || !featuresB || matches.length < 8) {
      return null;
    }

    try {
      const correspondences = buildCorrespondences(
        featuresA,
        featuresB,
        matches,
      );

      if (correspondences.length < 8) {
        return null;
      }

      const result = estimateFundamentalRANSAC(correspondences, {
        iterations: 1500,
        threshold: 1.5,
        imageWidth: featuresA.width,
        imageHeight: featuresA.height,
        gridColumns: 4,
        gridRows: 4,
      });

      debugLog("[Reconstruction] RANSAC:", {
        matches: correspondences.length,
        inliers: result.inliers.length,
        outliers: result.outliers.length,
        meanSampsonError: result.meanError,
      });

      return result;
    } catch (error) {
      console.error("[Reconstruction] Geometry estimation failed:", error);
      return null;
    }
  }, [featuresA, featuresB, matches]);

  // Custom pair rectification
  useEffect(() => {
    let cancelled = false;

    async function runRectification() {
      if (
        inputMode === "dataset" ||
        !imageA?.url ||
        !imageB?.url ||
        !geometry?.F ||
        geometry.inliers.length < 8
      ) {
        setRectification(null);
        return;
      }

      try {
        setRectification(null);

        const [imageElementA, imageElementB] = await Promise.all([
          loadImageElement(imageA.url),
          loadImageElement(imageB.url),
        ]);

        if (cancelled) {
          return;
        }

        const result = await rectifyUncalibratedPair({
          imageElementA,
          imageElementB,
          fundamentalMatrix: geometry.F,
          correspondences: geometry.inliers,
        });

        if (cancelled) {
          return;
        }

        setRectification(result);

        debugLog("[Reconstruction] Rectification diagnostics:", {
          correspondences: result.diagnostics.validCorrespondences,
          minimumVerticalError: result.diagnostics.minimumVerticalError,
          medianVerticalError: result.diagnostics.medianVerticalError,
          meanVerticalError: result.diagnostics.meanVerticalError,
          maximumVerticalError: result.diagnostics.maximumVerticalError,
        });
      } catch (error) {
        console.error("[Reconstruction] Rectification failed:", error);

        if (!cancelled) {
          setRectification(null);
        }
      }
    }

    runRectification();

    return () => {
      cancelled = true;
    };
  }, [imageA, imageB, geometry, inputMode]);

  // Focal self-calibration
  const focalSearch = useMemo(() => {
    if (inputMode === "dataset") {
      return null;
    }

    if (!geometry || !featuresA || !featuresB || geometry.inliers.length < 8) {
      return null;
    }

    try {
      const result = estimateSharedFocalLength({
        fundamentalMatrix: geometry.F,
        widthA: featuresA.width,
        heightA: featuresA.height,
        widthB: featuresB.width,
        heightB: featuresB.height,
      });

      if (!result) {
        return null;
      }

      debugLog("[Reconstruction] Focal self-calibration:", {
        referenceSize: result.referenceSize,
        coarseCandidates: result.coarseCandidates,
        fineCandidates: result.fineCandidates,
        coarseFocalLength: result.coarseBest.focalLength,
        bestFocalLength: result.best.focalLength,
        bestFocalRatio: result.best.focalRatio,
        bestScore: result.best.score,
        equalSingularPenalty: result.best.equalSingularPenalty,
        rankPenalty: result.best.rankPenalty,
        singularValues: result.best.singularValues,
      });

      return result;
    } catch (error) {
      console.error("[Reconstruction] Focal self-calibration failed:", error);
      return null;
    }
  }, [geometry, featuresA, featuresB, inputMode]);

  // Semi-dense epipolar matching
  useEffect(() => {
    let cancelled = false;

    async function runEpipolarMatching() {
      if (!imageA?.url || !imageB?.url || !geometry?.F) {
        setEpipolarMatches([]);
        return;
      }

      try {
        const [elementA, elementB] = await Promise.all([
          loadImageElement(imageA.url),
          loadImageElement(imageB.url),
        ]);

        const candidates = await detectSemiDenseCandidates(elementA, {
          maxCorners: 4000,
          qualityLevel: 0.005,
          minDistance: 5,
        });

        if (cancelled) {
          return;
        }

        const patchMatches = await matchEpipolarCandidates(
          elementA,
          elementB,
          candidates,
          geometry.F,
          {
            patchRadius: 4,
            searchStep: 2,
            minimumScore: 0.8,
            ambiguityMargin: 0.05,
          },
        );

        if (cancelled) {
          return;
        }

        const consistent = await filterMatchesByBidirectionalConsistency(
          elementA,
          elementB,
          patchMatches,
          geometry.F,
          {
            patchRadius: 4,
            searchStep: 2,
            maximumReturnDistance: 5,
          },
        );

        if (cancelled) {
          return;
        }

        setEpipolarMatches(consistent.accepted);

        debugLog(
          "[Reconstruction] Epipolar matches ready:",
          consistent.accepted.length,
        );
      } catch (error) {
        console.error("[Reconstruction] Epipolar matching failed:", error);

        if (!cancelled) {
          setEpipolarMatches([]);
        }
      }
    }

    runEpipolarMatching();

    return () => {
      cancelled = true;
    };
  }, [imageA, imageB, geometry]);

  const denseGeometry = useMemo(() => {
    if (!geometry || denseTracks.length === 0) {
      return null;
    }

    const result = filterCorrespondencesByEpipolarGeometry(
      denseTracks,
      geometry.F,
      2.0,
    );

    const denseErrors = denseTracks
      .map((track) => sampsonError(geometry.F, track))
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    debugLog("[Reconstruction] Dense epipolar filtering:", {
      input: denseTracks.length,
      accepted: result.accepted.length,
      rejected: result.rejected.length,
      minError: denseErrors[0],
      medianError: denseErrors[Math.floor(denseErrors.length / 2)],
      maxError: denseErrors[denseErrors.length - 1],
    });

    return result;
  }, [geometry, denseTracks]);

  // Camera pose and sparse triangulation
  const reconstruction = useMemo(() => {
    if (!geometry || !featuresA || !featuresB || geometry.inliers.length < 8) {
      return null;
    }

    try {
      let cameraMatrixA;
      let cameraMatrixB;
      let calibrationSource;

      if (inputMode === "dataset" && calibration) {
        cameraMatrixA = calibration.cameraMatrixA;
        cameraMatrixB = calibration.cameraMatrixB;
        calibrationSource = "Dataset";
      } else {
        const estimatedFocalLength = focalSearch?.best?.focalLength;

        if (!Number.isFinite(estimatedFocalLength)) {
          return null;
        }

        cameraMatrixA = createCameraMatrix(
          featuresA.width,
          featuresA.height,
          estimatedFocalLength,
        );

        cameraMatrixB = createCameraMatrix(
          featuresB.width,
          featuresB.height,
          estimatedFocalLength,
        );

        calibrationSource = "Estimated";
      }

      const essentialMatrix = fundamentalToEssential(
        geometry.F,
        cameraMatrixA,
        cameraMatrixB,
      );

      const pose = recoverCameraPose(
        essentialMatrix,
        geometry.inliers,
        cameraMatrixA,
        cameraMatrixB,
      );

      debugLog(
        "[Reconstruction] Essential matrix:",
        essentialMatrix.to2DArray(),
      );

      debugLog("[Reconstruction] Pose:", {
        candidateCounts: pose.candidateCounts,
        positiveDepth: pose.positiveDepthCount,
        triangulatedPoints: pose.points.length,
        translation: pose.t,
        rotation: pose.R.to2DArray(),
      });

      return {
        cameraMatrixA,
        cameraMatrixB,
        essentialMatrix,
        R: pose.R,
        t: pose.t,
        points: pose.points,
        positiveDepthCount: pose.positiveDepthCount,
        candidateCounts: pose.candidateCounts,
        calibrationSource,
        baseline: calibration?.baseline ?? null,
      };
    } catch (error) {
      console.error("[Reconstruction] Pose recovery failed:", error);
      return null;
    }
  }, [geometry, featuresA, featuresB, focalSearch, inputMode, calibration]);

  const coloredReconstruction = useMemo(() => {
    if (!reconstruction || !imageAIntensityData) {
      return reconstruction;
    }

    const coloredPoints = reconstruction.points.map((point) => ({
      ...point,
      intensity: sampleImageIntensity(
        imageAIntensityData,
        point.correspondence?.pointA,
      ),
    }));

    debugLog("[Reconstruction] Sparse intensity test:", {
      firstPoint: reconstruction.points[0],
      firstColoredPoint: coloredPoints[0],
      minIntensity: Math.min(...coloredPoints.map((point) => point.intensity)),
      maxIntensity: Math.max(...coloredPoints.map((point) => point.intensity)),
    });

    return {
      ...reconstruction,
      points: coloredPoints,
    };
  }, [reconstruction, imageAIntensityData]);

  // Reconstruction diagnostics
  const sparseReprojectionTest = useMemo(
    () =>
      evaluateSparseReprojection({
        geometry,
        reconstruction,
      }),
    [geometry, reconstruction],
  );

  const poseFundamentalTest = useMemo(
    () =>
      evaluatePoseFundamental({
        geometry,
        reconstruction,
      }),
    [geometry, reconstruction],
  );

  const essentialRoundTripTest = useMemo(
    () =>
      evaluateEssentialRoundTrip({
        geometry,
        reconstruction,
      }),
    [geometry, reconstruction],
  );

  const rawEssentialRoundTripTest = useMemo(
    () =>
      evaluateRawEssentialRoundTrip({
        geometry,
        reconstruction,
      }),
    [geometry, reconstruction],
  );

  const rawEssentialReprojectionTest = useMemo(
    () =>
      evaluateRawEssentialReprojection({
        geometry,
        featuresA,
        featuresB,
      }),
    [geometry, featuresA, featuresB],
  );

  useEffect(() => {
    if (!sparseReprojectionTest) {
      return;
    }

    const { metrics } = sparseReprojectionTest;

    debugLog("[Reconstruction] Sparse reprojection test:", {
      input: metrics.input,
      positiveDepth: metrics.positiveDepth,
      minError: metrics.min,
      medianError: metrics.median,
      maxError: metrics.max,
    });
  }, [sparseReprojectionTest]);

  useEffect(() => {
    if (!poseFundamentalTest) {
      return;
    }

    const { metrics } = poseFundamentalTest;

    debugLog("[Reconstruction] Pose-F test:", {
      input: metrics.input,
      minSampsonError: metrics.min,
      medianSampsonError: metrics.median,
      meanSampsonError: metrics.mean,
      maxSampsonError: metrics.max,
    });
  }, [poseFundamentalTest]);

  useEffect(() => {
    if (!essentialRoundTripTest) {
      return;
    }

    const { metrics } = essentialRoundTripTest;

    debugLog("[Reconstruction] E-F round-trip test:", {
      input: metrics.input,
      minSampsonError: metrics.min,
      medianSampsonError: metrics.median,
      meanSampsonError: metrics.mean,
      maxSampsonError: metrics.max,
    });
  }, [essentialRoundTripTest]);

  useEffect(() => {
    if (!rawEssentialRoundTripTest) {
      return;
    }

    const { metrics } = rawEssentialRoundTripTest;

    debugLog("[Reconstruction] Raw E-F round-trip test:", {
      input: metrics.input,
      minSampsonError: metrics.min,
      medianSampsonError: metrics.median,
      meanSampsonError: metrics.mean,
      maxSampsonError: metrics.max,
    });
  }, [rawEssentialRoundTripTest]);

  useEffect(() => {
    if (!rawEssentialReprojectionTest) {
      return;
    }

    const { metrics } = rawEssentialReprojectionTest;

    debugLog("[Reconstruction] Raw-E reprojection test:", {
      input: metrics.input,
      positiveDepth: metrics.positiveDepth,
      minError: metrics.min,
      medianError: metrics.median,
      maxError: metrics.max,
    });
  }, [rawEssentialReprojectionTest]);

  // Semi-dense triangulation
  const semiDenseReconstruction = useMemo(() => {
    if (!reconstruction || epipolarMatches.length === 0) {
      return null;
    }

    const triangulated = triangulateCorrespondences(
      epipolarMatches,
      reconstruction.cameraMatrixA,
      reconstruction.cameraMatrixB,
      reconstruction.R,
      reconstruction.t,
    );

    const filtered = filterTriangulatedByReprojection(
      triangulated.accepted,
      reconstruction.cameraMatrixA,
      reconstruction.cameraMatrixB,
      reconstruction.R,
      reconstruction.t,
      3.0,
    );

    const allReprojectionErrors = [...filtered.accepted, ...filtered.rejected]
      .map((point) => point.reprojectionError)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    const acceptedErrors = filtered.accepted
      .map((point) => point.reprojectionError)
      .filter(Number.isFinite)
      .sort((a, b) => a - b);

    debugLog("[Reconstruction] Semi-dense triangulation:", {
      input: epipolarMatches.length,
      positiveDepth: triangulated.accepted.length,
      reprojectionAccepted: filtered.accepted.length,
      reprojectionRejected: filtered.rejected.length,
      minReprojectionError:
        allReprojectionErrors.length > 0 ? allReprojectionErrors[0] : null,
      medianReprojectionError:
        allReprojectionErrors.length > 0
          ? allReprojectionErrors[Math.floor(allReprojectionErrors.length / 2)]
          : null,
      maxReprojectionError:
        allReprojectionErrors.length > 0
          ? allReprojectionErrors[allReprojectionErrors.length - 1]
          : null,
      medianAcceptedError:
        acceptedErrors.length > 0
          ? acceptedErrors[Math.floor(acceptedErrors.length / 2)]
          : null,
    });

    return {
      ...filtered,
      points: filtered.accepted,
    };
  }, [epipolarMatches, reconstruction]);

  const coloredSemiDenseReconstruction = useMemo(() => {
    if (!semiDenseReconstruction || !imageAIntensityData) {
      return semiDenseReconstruction;
    }

    return {
      ...semiDenseReconstruction,
      points: semiDenseReconstruction.points.map((point) => ({
        ...point,
        intensity: sampleImageIntensity(
          imageAIntensityData,
          point.correspondence?.pointA,
        ),
      })),
    };
  }, [semiDenseReconstruction, imageAIntensityData]);

  return {
    featureStatus,
    featuresA,
    featuresB,
    matches,
    geometry,
    focalSearch,
    rectification,
    reconstruction: coloredReconstruction,
    semiDenseReconstruction: coloredSemiDenseReconstruction,
    denseTracks,
    denseGeometry,
    epipolarMatches,
    denseDisparity,
    filteredDenseDisparity,
    propagatedDenseDisparity,
    surfaceDenseDisparity,
    densePointCloud,
    denseMesh,
    denseStatus,
    diagnostics: {
      sparseReprojection: sparseReprojectionTest,
      poseFundamental: poseFundamentalTest,
      essentialRoundTrip: essentialRoundTripTest,
      rawEssentialRoundTrip: rawEssentialRoundTripTest,
      rawEssentialReprojection: rawEssentialReprojectionTest,
    },
  };
}
